import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import BlankLayout from '../layouts/blank-layout.vue'
import TerminalLayout from '../layouts/TerminalLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

import { useAuthStore } from '@/stores/auth.store'
import { useMutationStore } from '@/stores/mutation.store'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },

  {
    name: 'demo',
    path: '/demo',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/demo/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/demo/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/demo/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/demo/users/UsersPage.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/demo/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/demo/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/demo/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/demo/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/demo/faq/FaqPage.vue'),
      },
    ],
  },

  {
    name: '',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, [ ], 'not-found-simple') },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, [ ], 'not-found-simple') },
        component: () => import('../pages/scms/dashboard/Dashboard.vue'),
      },
    ],
  },

  {
    path: '/erp',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'general', 'assets'], 'not-found-simple') },
    children: [
      {
        name: 'article',
        path: 'article',
        component: () => import('../pages/erp/article/article/Article.vue'),
      },
    ],
  },
  
  {
    path: '/password',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'user', 'password-generator'], 'not-found-simple') },
    children: [
      {
        name: 'password',
        path: '',
        component: () => import('../pages/scms/password/PasswordGenerator.vue'),
      },
    ],
  },
  
  {
    path: '/project',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'project'], 'not-found-simple') },
    children: [
      {
        name: 'project',
        path: '',
        component: () => import('../pages/scms/operational-planning/project/Project.vue'),
      },
    ],
  },

  {
    path: '/tasks',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'project'], 'not-found-simple') },
    children: [
      {
        name: 'tasks',
        path: '',
        component: () => import('../pages/scms/tasks/Tasks.vue'),
      },      
      {
        name: 'task-show',
        path: 'show',
        component: () => import('../pages/scms/tasks/ShowTask.vue'),
      },
    ],
  },


  {
    path: '/article',
    name: 'article_container',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'article'], '401') },
    children: [
      {
        name: 'article',
        path: 'overview',
        component: () => import('../pages/scms/article/article/Article.vue'),
      },
      {
        name: 'category',
        path: 'category',
        component: () => import('../pages/scms/article/article_category/ArticleCategory.vue'),
      },
    ],
  },

  {
    path: '/extern',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'extern'], 'not-found-simple') },
    children: [  
      {
        name: 'chp',
        path: 'chp',
        component: () => import('../pages/scms/extern/chp/CHP.vue'),
      },
    ],
  },

  {
    path: '/operations',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'operations'], 'not-found-simple') },
    children: [
      {
        name: 'operations-job',
        path: 'job',
        component: () => import('../pages/scms/operational-planning/operations/job/OperationJob.vue'),
      },
      {
        name: 'operations-report',
        path: 'report',
        component: () => import('../pages/scms/operational-planning/operations/report/OperationReport.vue'),
      },
      {
        name: 'operations-export',
        path: 'export',
        component: () => import('../pages/scms/operational-planning/operations/export/OperationExport.vue'),
      },
    ],
  },

  {
    path: '/assets',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'general', 'assets'], 'not-found-simple') },
    children: [
      {
        name: 'company',
        path: 'company',
        component: () => import('../pages/scms/assets/company/Company.vue'),
      },
      {
        name: 'property',
        path: 'property',
        component: () => import('../pages/scms/assets/property/Property.vue'),
      },
      {
        name: 'rental-unit',
        path: 'rental-unit',
        component: () => import('../pages/scms/assets/rental-unit/RentalUnit.vue'),
      },
      {
        name: 'parking-lot',
        path: 'parking-lot',
        component: () => import('../pages/scms/assets/parking-lot/ParkingLot.vue'),
      },
      {
        name: 'cellar',
        path: 'cellar',
        component: () => import('../pages/scms/assets/cellar/Cellar.vue'),
      },
      {
        name: 'apartment-type',
        path: 'apartment-type',
        component: () => import('../pages/scms/assets/apartment-type/ApartmentType.vue'),
      },
      
      {
        name: 'customer',
        path: 'customer',
        component: () => import('../pages/scms/assets/customer/Customer.vue'),
      },
    ]
  },

  {
    path: '/racquet-sport',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'racquet-sport'], 'not-found-simple') },
    children: [
      {
        name: 'racquet-sport/brand',
        path: 'brand',
        component: () => import('../pages/scms/racquet-sport/brand/Brand.vue'),
      },
      {
        name: 'racquet-sport/model',
        path: 'model',
        component: () => import('../pages/scms/racquet-sport/model/Model.vue'),
      },
      {
        name: 'racquet-sport/racquet',
        path: 'racquet',
        component: () => import('../pages/scms/racquet-sport/racquet/Racquet.vue'),
      },
      {
        name: 'racquet-sport/string',
        path: 'string',
        component: () => import('../pages/scms/racquet-sport/string/String.vue'),
      },
      {
        name: 'racquet-sport/stringing',
        path: 'stringing',
        component: () => import('../pages/scms/racquet-sport/stringing/Stringing.vue'),
      },
    ]
  },

  {
    path: '/management',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'general', 'management'], 'not-found-simple') },
    children: [
      {
        name: 'tenancy',
        path: 'tenancy',
        component: () => import('@/pages/scms/rental_management/tenancy/Tenancy.vue'),
      },
      {
        name: 'tenancy-business',
        path: 'tenancy-business',
        component: () => import('@/pages/scms/rental_management/tenancy-business/Tenancy.vue'),
      },
      {
        name: 'rental-contract',
        path: 'rental-contract',
        component: () => import('@/pages/scms/rental_management/rental-contract/RentalContract.vue'),
      },
      {
        name: 'termination-agreement',
        path: 'termination-agreement',
        component: () => import('@/pages/scms/rental_management/termination-agreement/TerminationAgreement.vue'),
      },
      {
        name: 'tenant',
        path: 'tenant',
        component: () => import('@/pages/scms/rental_management/tenant/Tenant.vue'),
      },

      {
        name: 'rpc',
        path: 'rpc',
        component: () => import('@/pages/scms/rental_management/rent-price-calculator/RentPriceCalculator.vue'),
      },
    ]
  },
  
  {
    path: '/renting',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'tenant-suggestions'], 'not-found-simple') },
    children: [
      {
        name: 'tenant-suggestion-overview',
        path: 'suggestion/overview',
        component: () => import('@/pages/scms/renting/suggestion/overview/Overview.vue'),
      },
      {
        name: 'tenant-suggestion-add',
        path: 'suggestion/add',
        component: () => import('@/pages/scms/renting/suggestion/add/Add.vue'),
      },
    ]
  },

  {
    path: '/appointment',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'appointment'], 'not-found-simple') },
    children: [
      {
        name: 'appointment/service',
        path: 'service',
        component: () => import('@/pages/scms/appointment/service/AppointmentService.vue'),
      },
      {
        name: 'appointment/user',
        path: 'user',
        component: () => import('@/pages/scms/appointment/user/AppointmentUser.vue'),
      },
    ]
  },

  {
    path: '/accounting',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'general'], 'not-found-simple') },
    children: [
      {
        name: 'receivable',
        path: 'receivable',
        component: () => import('@/pages/scms/accounting/receivable/Receivable.vue'),
      },
      {
        name: 'credit-note',
        path: 'credit-note',
        component: () => import('@/pages/scms/accounting/credit-note/CreditNote.vue'),
      },
      {
        name: 'invoice',
        path: 'invoice',
        component: () => import('@/pages/scms/accounting/invoice/Invoice.vue'),
      },
      {
        name: 'bank-transfers',
        path: 'bank-transfers',
        component: () => import('@/pages/scms/accounting/bank-transfers/BankTransfers.vue'),
      },
      {
        name: 'export',
        path: 'export',
        component: () => import('@/pages/scms/accounting/export/Export.vue'),
      },

      {
        name: 'purchase-price-request',
        path: 'purchase-price-request',
        component: () => import('@/pages/scms/accounting/purchase-price-request/PurchasePriceRequest.vue'),
      },
      {
        name: 'banking',
        path: 'banking',
        beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['banking'], 'not-found-simple') },
        component: () => import('@/pages/scms/accounting/banking/Banking.vue'),
      },
    ]
  },
  
  {
    path: '/report',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'report'], 'not-found-simple') },
    children: [
      {
        name: 'report-general',
        path: 'general',
        component: () => import('@/pages/scms/reports/general/GeneralReport.vue'),
      },
      {
        name: 'report-overview',
        path: 'overview',
        component: () => import('@/pages/scms/reports/overview/Overview.vue'),
      },
      {
        name: 'report-apartments',
        path: 'apartments',
        component: () => import('@/pages/scms/reports/apartments/ApartmentReport.vue'),
      },
      {
        name: 'report-rent-arrears',
        path: 'rent-arrears',
        component: () => import('@/pages/scms/reports/rent-arrears/RentArrearsReport.vue'),
      },
      {
        name: 'report-renting',
        path: 'renting',
        component: () => import('@/pages/scms/reports/renting/RentingReport.vue'),
      },
      {
        name: 'report-receivables',
        path: 'receivables',
        component: () => import('@/pages/scms/reports/receivables/ReceivablesReport.vue'),
      },
      {
        name: 'report-rental-contracts',
        path: 'rental-contracts',
        component: () => import('@/pages/scms/reports/rental-contracts/RentalContractReport.vue'),
      },
      {
        name: 'report-tenants',
        path: 'tenants',
        component: () => import('@/pages/scms/reports/tenants/TenantsReport.vue'),
      },
      {
        name: 'report-analytics',
        path: 'analytics',
        component: () => import('@/pages/scms/reports/analytics/AnalyticsReport.vue'),
      },
      {
        name: 'datev',
        path: 'datev',
        component: () => import('@/pages/scms/reports/datev/Datev.vue'),
      },
      {
        name: 'report-weekly',
        path: 'weekly',
        component: () => import('@/pages/scms/reports/weekly-report/WeeklyReport.vue'),
      },
      {
        name: 'report-renewal',
        path: 'renewal',
        component: () => import('@/pages/scms/reports/renewal/Renewal.vue'),
      },
      {
        name: 'report-mailer',
        path: 'mailer',
        component: () => import('@/pages/scms/reports/mailer/Mailer.vue'),
      },
      {
        name: 'report-map',
        path: 'map',
        component: () => import('@/pages/scms/reports/map/Map.vue'),
      }
    ]
  },
  
  {
    path: '/import',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'property'], 'not-found-simple') },
    children: [
      {
        name: 'import-bank-transfer',
        path: 'bank-transfer',
        component: () => import('@/pages/scms/import/bank-transfer/ImportBankTransfer.vue'),
      },
    ]
  },

  {
    path: '/handover',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'handover'], 'not-found-simple') },
    children: [
      {
        name: 'handover-protocol',
        path: 'protocol',
        component: () => import('@/pages/scms/handover/new_protocol/HandoverProtocol.vue'),
      },
    ]
  },

  {
    path: '/vacation-planning',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'vacation-planning'], '401') },
    children: [
      {
        name: 'vacation-planning-overview',
        path: 'overview',
        component: () => import('../pages/scms/operational-planning/vacation-planning/overview/Overview.vue'),
      },
      {
        name: 'vacation-planning-application',
        path: 'application',
        component: () => import('../pages/scms/operational-planning/vacation-planning/application/Application.vue'),
      },
      {
        name: 'vacation-planning-examination',
        path: 'examination',
        component: () => import('../pages/scms/operational-planning/vacation-planning/examination/Examination.vue'),
      },
    ]
  },


  
  {
    path: '/time-tracking',
    name: 'time_tracking',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'time-tracking'], '401') },
    children: [
      {
        name: 'time-tracking-overview',
        path: 'overview',
        component: () => import('../pages/scms/operational-planning/time-tracking/overview/Overview.vue'),
      },
      {
        name: 'time-tracking-track',
        path: 'track',
        component: () => import('../pages/scms/operational-planning/time-tracking/track/Track.vue'),
      },
      {
        name: 'time-tracking-sick-notes',
        path: 'sick-notes',
        component: () => import('../pages/scms/operational-planning/time-tracking/sick-notes/SickNotes.vue'),
      },
      {
        name: 'time-tracking-reports',
        path: 'reports',
        component: () => import('../pages/scms/operational-planning/time-tracking/reports/Reports.vue'),
      },
    ],
  },

  {
    path: '/time-tracking/terminal',
    name: 'time_tracking/terminal',
    component: TerminalLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'time-tracking-terminal'], '401') },
    children: [
      {
        name: 'terminal',
        path: '',
        component: () => import('../pages/scms/operational-planning/time-tracking/terminal/Terminal.vue'),
      },
    ],
  },

  {
    path: '/scouting',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'scouting', 'deficiency-tracking'], 'not-found-simple') },
    children: [
      {
        name: 'deficiency-tracking/overview',
        path: 'deficiency-tracking/overview',
        component: () => import('@/pages/scms/deficiency-tracking/DeficiencyTracking.vue'),
      },
      {
        name: 'property-scouting-new-version',
        path: 'new-version',
        component: () => import('@/pages/scms/scouting/property_new/NewPropertyScouting.vue'),
      },
    ]
  },

  {
    name: 'viewing',
    path: '/viewing',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'viewing'], 'not-found-simple') },
    children: [
      {
        name: 'viewing-apartment-overview',
        path: 'apartment/overview',
        component: () => import('@/pages/scms/viewing/apartments/overview/Overview.vue'),
      },
      {
        name: 'viewing-apartment-add',
        path: 'apartment/add',
        component: () => import('@/pages/scms/viewing/apartments/createedit/CreateEdit.vue'),
      },
      {
        name: 'viewing-handover-protocol',
        path: 'handover/protocol',
        component: () => import('@/pages/scms/viewing/handover/protocol/Protocol.vue'),
      },
    ]
  },
  
  {
    path: '/settings',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'settings'], 'not-found-simple') },
    children: [
      {
        name: 'settings',
        path: '',
        component: () => import('@/pages/scms/settings/Settings.vue'),
      },
    ]
  },

  {
    path: '/database',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'database'], 'not-found-simple') },
    children: [
      {
        name: 'cpi',
        path: 'cpi',
        component: () => import('@/pages/scms/database/cpi/CPI.vue'),
      },
    ]
  },

  {
    name: 'cms',
    path: '/cms',
    component: AppLayout,
    children: [
      {
        name: 'cms/media-library',
        path: 'media-library',
        component: () => import('@/pages/scms/cms/media-library/MediaLibrary.vue'),
        beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'user', 'cms'], 'not-found-simple') },
      },
    ]
  },

  {
    name: 'analytics',
    path: '/analytics',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'analytics'], 'not-found-simple') },
    children: [
      {
        name: 'analytics/visitor',
        path: 'visitor',
        component: () => import('@/pages/scms/analytics/visitor/VisitorAnalytics.vue'),
      },
    ]
  },

  {
    name: 'letter-template',
    path: '/letter-template',
    component: BlankLayout,
    children: [
      {
        name: 'letter-template/short-message',
        path: 'short-message',
        component: () => import('@/pages/scms/letter-template/short-message/ShortMessage.vue'),
      },
    ]
  },

  {
    path: '/employee',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin', 'employee'], 'not-found-simple') },
    children: [
      {
        name: 'employee-overview',
        path: 'employee/overview',
        component: () => import('@/pages/scms/employee/overview/EmployeeOverview.vue'),
      },
      {
        name: 'employee-calendar',
        path: 'employee/calendar',
        component: () => import('@/pages/scms/employee/calendar/EmployeeCalendar.vue'),
      },
      {
        name: 'employee-edit',
        path: 'employee/edit',
        component: () => import('@/pages/scms/employee/overview/EmployeeOverview.vue'),
      },
    ]
  },

  {
    path: '/notification',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, [ ], 'not-found-simple') },
    children: [
      {
        name: 'notification/overview',
        path: 'overview',
        component: () => import('@/pages/scms/notification/Notification.vue'),
      }
    ]
  },

  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    beforeEnter: (to:any, from:any, next:any) => { isAllowed(to, from, next, ['admin'], 'not-found-simple') },
    children: [
      {
        name: 'admin/user',
        path: 'user',
        component: () => import('@/pages/scms/admin/user/User.vue'),
      },
      {
        name: 'admin/settings',
        path: 'settings',
        component: () => import('@/pages/scms/admin/settings/Settings.vue'),
      },
    ]
  },

  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },

  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

const isAllowed = (to:any, from:any, next:any, permissions:Array<string>, reditectTo:string) => {
  const authStore = useAuthStore()

  if (to.name == 'login') {
    if(authStore?.token) {
      next({ name: '' })
    } else {
      next();
    }
  } else {
    let check = checkPermissions(permissions)

    if(check.allowed && authStore?.token) {
      next();
    } else {
      if(authStore?.token) {
        next({ name: reditectTo })
      } else {
        next({ name: 'login' })
      }
    }
  }
}

const checkPermissions = (permissions:Array<string>) => {
  const authStore = useAuthStore()

  // return { allowed: true, redirectTo: null }

  /*
  if(authStore?.user?.role?.name == 'admin') {
    return { allowed: true, redirectTo: null };
  }
  */

  if(permissions.length == 0 || !permissions) {
    return { allowed: true, redirectTo: null };
  } else if(permissions.length > 0) {
    if(authStore?.token && authStore?.user?.role) {
      for(let permission of permissions) {
        if(authStore?.user?.role?.permissions.map((data: any) => { return data.name }).indexOf(permission) > -1) {
          return { allowed: true, redirectTo: null };
        }
      }

      return { allowed: false, redirectTo: '' };
    } else {
      return { allowed: false, redirectTo: 'login' };
    }
  } else {
    return { allowed: false, redirectTo: '' };
  }
}


router.beforeEach(async (to:any, from:any, next:any) => {
  const authStore = useAuthStore();

  // NEW LOGOUT FEATURE
  console.log(`Seconds to logout: ${ ( new Date( authStore.exp ) - new Date().getTime() ) / 1000 }`)
  if( new Date( authStore.exp ).getTime() < new Date().getTime() ) {
    console.log('logout!!!')
    authStore.LOGOUT().then(() => {
      next({ name: 'login' })
    });
  }

  const mutationStore = useMutationStore();

  let redirect = mutationStore.auth_guard({name: to?.name, query: to?.query});

  if(!redirect) {
    next(false);
  } else {
    if (to.name == 'login') {
      if(authStore?.token) {
        next();
      } else {
        next();
      }
    } else {
      next();
    }
  }
})

export default router
